import {localeFromLanguages, resolveRedirectLocale} from './locale-choice';
import {DEFAULT_LOCALE, isLocale, type Locale} from './locales';

// Addresses of the VitePress documentation in each language: `/pages/docs/fonts`,
// `/pages/es/docs/fonts`. Shared by the app, the Workers build, the sitemap, and
// the VitePress config and theme, so it stays free of DOM, Node, and Vue imports.

/**
 * The single switch for documentation languages. A language listed here gets its
 * `/pages/<locale>/docs/` pages built, the docs language menu and redirects, hreflang
 * and sitemap alternates, links from the app, and the strict translation tests.
 * List a language only once every docs page is translated (LOP-163).
 */
export const DOCS_LOCALES: readonly Locale[] = [DEFAULT_LOCALE, 'es', 'de'];

export interface DocsPage {
    locale: Locale;
    /** Address below the docs root, the way VitePress serves it: `''`, `fonts`, `pricing/`. */
    docPath: string;
}

function docsRoot(locale: Locale): string {
    return locale === DEFAULT_LOCALE ? '/pages/docs/' : `/pages/${locale}/docs/`;
}

/**
 * Builds the address of a documentation page in a language: `pricing/` + `es` →
 * `/pages/es/docs/pricing/`. Languages whose docs are not published get English.
 */
export function localizeDocsPath(docPath: string, locale: Locale, published: readonly Locale[] = DOCS_LOCALES): string {
    return docsRoot(published.includes(locale) ? locale : DEFAULT_LOCALE) + docPath;
}

/** Reads the language and page of a documentation address; null for every other page. */
export function parseDocsPath(pathname: string): DocsPage | null {
    const match = pathname.match(/^\/pages\/(?:([a-z]{2})\/)?docs(?:\/(.*))?$/);
    if (!match) return null;
    const [, prefix, rest = ''] = match;
    const locale = prefix ?? DEFAULT_LOCALE;
    if (!isLocale(locale) || (prefix && locale === DEFAULT_LOCALE)) return null;
    return {locale, docPath: rest.replace(/(^|\/)index\.html$/, '$1').replace(/\.html$/, '')};
}

/** `docs/fonts.md` → English `fonts.md`; `es/docs/pricing/index.md` → Spanish `pricing/index.md`. */
export function docsPageFile(relativePath: string): {locale: Locale; file: string} | null {
    const match = relativePath.match(/^(?:([a-z]{2})\/)?docs\/(.+\.md)$/);
    if (!match) return null;
    const [, prefix, file] = match;
    const locale = prefix ?? DEFAULT_LOCALE;
    if (!isLocale(locale) || (prefix && locale === DEFAULT_LOCALE)) return null;
    return {locale, file};
}

/** `pricing/index.md` → `pricing/`, `fonts.md` → `fonts`: the clean URL VitePress serves. */
export function docsPathOfFile(file: string): string {
    return file.replace(/(^|\/)index\.md$/, '$1').replace(/\.md$/, '');
}

/**
 * Languages a docs page is published in: English, then each published language
 * whose translation `pages/<locale>/docs/<file>` exists among `pages`.
 */
export function docsPageLocales(
    file: string,
    pages: Iterable<string>,
    published: readonly Locale[] = DOCS_LOCALES,
): Locale[] {
    const existing = new Set(pages);
    return published.filter((locale) => locale === DEFAULT_LOCALE || existing.has(`${locale}/docs/${file}`));
}

/**
 * Address the docs page should redirect to, or null to stay. Same rules as the
 * localized SPA pages (`resolveRedirectLocale`); a stored or browser language whose
 * docs are not published counts as English.
 */
export function resolveDocsRedirectPath(input: {
    path: string;
    storedLocale: Locale | null;
    languages: readonly string[];
    published?: readonly Locale[];
}): string | null {
    const {path, storedLocale, languages, published = DOCS_LOCALES} = input;
    const page = parseDocsPath(path);
    if (!page) return null;
    const browserLocale = localeFromLanguages(languages);
    const publishedOr = (locale: Locale | null, fallback: Locale | null): Locale | null =>
        locale === null || published.includes(locale) ? locale : fallback;
    const target = resolveRedirectLocale({
        pageLocale: page.locale,
        storedLocale: publishedOr(storedLocale, DEFAULT_LOCALE),
        browserLocale: publishedOr(browserLocale, null),
    });
    return target ? localizeDocsPath(page.docPath, target, published) : null;
}

/** A move between two docs addresses that is a language switch: same page, another language. */
export function docsLanguageSwitch(fromPath: string, toPath: string): {from: Locale; to: Locale} | null {
    const from = parseDocsPath(fromPath);
    const to = parseDocsPath(toPath);
    if (!from || !to || from.docPath !== to.docPath || from.locale === to.locale) return null;
    return {from: from.locale, to: to.locale};
}

/**
 * `/pages/es/` → `/pages/es/docs/`: a docs language has no Journal home, so its root
 * opens its docs index. `public/_redirects` does the same on the server.
 */
export function docsLanguageRootPath(pathname: string): string | null {
    const prefix = pathname.match(/^\/pages\/([a-z]{2})\/?$/)?.[1];
    return prefix && prefix !== DEFAULT_LOCALE && isLocale(prefix) ? `/pages/${prefix}/docs/` : null;
}
